import { TRIAL_DAYS } from "@/brand/plan-numbers"

import { DATA_COUNTRY } from "../../content/pages/trust-facts"
import {
  capKm,
  displayYear,
  formatCheckedOn,
  HOME_FAQ,
  HOME_JOBS,
  HOME_PLANS,
  HOME_STEPS,
} from "./home-content"

/**
 * Every sentence the home page prints, as one object per language. English values come from
 * home-content.ts (the copy.md source of truth) and the strings that used to sit in the JSX;
 * the Chinese object is `brand/content/zh/home.ts`. Numbers, rates and links are not here: they
 * come from jurisdiction-au and home-content in both languages.
 */
export interface HomeCopy {
  trialBadge: string
  h1: string
  lead: string
  startTrial: string
  seeHow: string
  appsGroupLabel: string
  comingSoon: string
  statLabels: { rate: (year: string) => string; cap: string; records: string }
  /** Figure and label share one value so the region always comes from the trust facts. */
  regionStat: { figure: string; label: string }
  recordsFigure: string
  jobsTitle: string
  jobs: { title: string; body: string }[]
  howTitle: string
  steps: { title: string; body: string }[]
  howMore: string
  claimTitle: string
  claimSources: { individuals: string; business: string }
  claimAnswer: (p: { capKm: string; year: string; cents: number }) => string
  lastReviewed: (date: string) => string
  methodsTable: {
    caption: string
    columns: string[]
    rows: { header: string; cells: string[] }[]
  }
  recordsTitle: string
  recordsStored: string
  /** Text before and after the link to the trust page. */
  recordsStoredBody: [before: string, after: string]
  trustLinkText: string
  fiveYearTitle: string
  fiveYearBody: string
  atoRecordsLabel: string
  exportTitle: string
  exportBody: string
  pricingTitle: string
  /** Shown after a monthly price. */
  perMonth: string
  draftBadge: string
  plans: { name: string; note: string }[]
  draftNote: string
  pricingMore: string
  toolsTitle: string
  freeToolsHeading: string
  guidesHeading: string
  compareTitle: string
  faqTitle: string
  faq: { question: string; answer: string }[]
  faqMore: string
  bandTitle: string
  bandDescription: string
  formatCheckedOn: (iso: string) => string
  displayYear: (fy: string) => string
}

export const HOME_COPY_EN: HomeCopy = {
  trialBadge: `${TRIAL_DAYS}-day free trial. No card needed.`,
  h1: "Mileage, receipts and invoices for Australian sole traders",
  lead: "Record work trips on your phone, photograph receipts and send tax invoices. Everything stays in one place, ready for tax time and your accountant.",
  startTrial: "Start free trial",
  seeHow: "See how it works",
  appsGroupLabel: "Mobile apps coming soon",
  comingSoon: "Coming soon",
  statLabels: {
    rate: (year) => `${year} rate`,
    cap: "cap per car",
    records: "ATO record rule",
  },
  regionStat: { figure: DATA_COUNTRY, label: "data stored" },
  recordsFigure: "5 years",
  jobsTitle: "One app for the three jobs that eat your evenings",
  jobs: HOME_JOBS.map(({ title, body }) => ({ title, body })),
  howTitle: "How it works",
  steps: HOME_STEPS,
  howMore: "How Kestly works, step by step",
  claimTitle: "Two ways to claim car expenses. We keep both ready.",
  claimSources: {
    individuals: "ATO, cents per kilometre method (individuals)",
    business: "ATO, cents per kilometre method (business)",
  },
  claimAnswer: ({ capKm: cap, year, cents }) =>
    `Sole traders can claim work use of a car with the cents per kilometre method (up to ${cap} business km per car a year) or the logbook method (actual costs for the business-use share). For ${year} the cents per kilometre rate is ${cents} cents.`,
  lastReviewed: (date) => `Last reviewed ${date}`,
  methodsTable: {
    caption: "Two ways to claim car expenses",
    columns: ["", "Cents per km", "Logbook"],
    rows: [
      { header: "Best when", cells: ["Few km, simple claim", "Many km, high running costs"] },
      { header: "You keep", cells: ["How you worked out the km", "12-week logbook and receipts"] },
      { header: "Limit", cells: [`${capKm} km per car`, "None"] },
    ],
  },
  recordsTitle: "Your records, kept properly",
  recordsStored: `Stored in ${DATA_COUNTRY}`,
  recordsStoredBody: [
    "Account data and receipt files are held in Australia. Read the ",
    " for the full list.",
  ],
  trustLinkText: "security and privacy page",
  fiveYearTitle: "The ATO's 5-year rule",
  fiveYearBody:
    "The ATO says to keep most business records for 5 years. Export them any time. After a paid plan lapses we keep your data for 3 months at most and remind you several times to export it before it is removed from your account.",
  atoRecordsLabel: "ATO, keeping your business records",
  exportTitle: "Yours to take",
  exportBody:
    "Export trips, receipts and invoices as CSV or PDF any time, and for up to 3 months after a paid plan lapses.",
  pricingTitle: "Simple pricing",
  perMonth: " / month",
  draftBadge: "Draft prices",
  plans: HOME_PLANS.map(({ name, note }) => ({ name, note })),
  draftNote: "Draft, not final.",
  pricingMore: "See all plans, yearly prices and the team plan",
  toolsTitle: "Free tools and guides",
  freeToolsHeading: "Free tools",
  guidesHeading: "Guides",
  compareTitle: "Compare Kestly with other options",
  faqTitle: "Common questions",
  faq: HOME_FAQ,
  faqMore: "More questions and answers",
  bandTitle: `Start with a ${TRIAL_DAYS}-day free trial`,
  bandDescription: "No card needed. Free plan after the trial if you do not subscribe.",
  formatCheckedOn,
  displayYear,
}
